import React, { useState, useEffect, useContext, useRef, useCallback } from 'react';
import { useLocation, useHistory } from 'react-router-dom';
import GameSummary from './GameSummary';
import BlogSummary from './Components/BlogSummary';
import PlaySummary from './Components/PlaySummary';
import PremiumSummary from './Components/PremiumSummary';
import NewsSummary from './Components/NewsSummary';
import FriendsList from './Components/friendslist';
import PuzzleSummary from './PuzzleSummary';
import chessslogo from '../images/chessslogo.png';
import "../App.css";
import { ChessSocialContext } from '../util/ChessSocialContext';
import { usePremium } from '../util/PremiumFilter'; // Import the premium context hook
import constants, { DIFFICULTY, allowedEcoGroups, HIDDEN_TAGS } from '../util/constants';
import WelcomeLoginModal from '../components/modals/WelcomeLoginModal';

import { fetchGames } from '../util/api/game';
import { doc, getDoc, query, where, collection, getDocs, orderBy, limit, startAfter } from 'firebase/firestore';
import { firestore } from '../firebase';

// Connect component to Redux store to get user information
import { connect } from 'react-redux';


const Portal = (props) => {
  const history = useHistory();
  const location = useLocation();
  const [games, setGames] = useState([]);
  const [isLoadingGames, setIsLoadingGames] = useState(true); // Add loading state
  const [activeTab, setActiveTab] = useState('NEW');
  // Add missing state variables
  const [selectedDifficulty, setSelectedDifficulty] = useState(localStorage.getItem('portalDifficulty') || 'All Levels');
  const [showDifficultyDropdown, setShowDifficultyDropdown] = useState(false);
  // Add state for welcome modal
  const [showWelcomeModal, setShowWelcomeModal] = useState(false);

  // Add new state variables for openings dropdown
  const [showOpeningsDropdown, setShowOpeningsDropdown] = useState(false);
  const [selectedOpening, setSelectedOpening] = useState(null);
  const [openingSearchTerm, setOpeningSearchTerm] = useState('');
  const [isOpeningSelected, setIsOpeningSelected] = useState(false);

  // Add pagination state variables
  const [nextCursor, setNextCursor] = useState(null);
  const [hasMore, setHasMore] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);

  // Reference for scroll detection
  const contentEndRef = useRef(null);

  // Remove the local state for premium content
  // const [showPremiumContent, setShowPremiumContent] = useState(false);
  const { isSidebarCollapsed } = useContext(ChessSocialContext);
  // Use the premium context instead
  const { isPremiumUser, togglePremiumStatus, premiumFeatures } = usePremium();

  // Show welcome modal only after login, not on subsequent visits
  useEffect(() => {
    // Check if we just navigated from the login page
    const fromLogin = location.state && location.state.fromLogin;
    const hasShownModalThisSession = localStorage.getItem('welcomeModalShown');

    if (fromLogin && !hasShownModalThisSession) {
      // Show modal after a 500ms delay, but only if not shown before in this session
      const timer = setTimeout(() => {
        setShowWelcomeModal(true);
        // Mark that we've shown the modal for this session
        localStorage.setItem('welcomeModalShown', 'true');
      }, 500);

      return () => clearTimeout(timer);
    }
  }, [location]);

  // Clear the modal shown flag when user logs out
  useEffect(() => {
    if (!props.user || !props.user.authenticated) {
      localStorage.removeItem('welcomeModalShown');
    }
  }, [props.user]);

  // Add the missing getTagKeyFromTabName function
  const getTagKeyFromTabName = (tabName) => {
    // If the tab is one of the special tabs, return null
    if (['NEW', 'FOLLOWING', 'TRENDING'].includes(tabName)) {
      return null;
    }

    // Handle Openings tab specifically
    if (tabName === 'OPENINGS') {
      return 'Openings';
    }

    // Convert the incoming tab name to title case since our constants are in title case
    // E.g. "PUZZLE" -> "Puzzle", "THE-PROS" -> "The Pros"
    const titleCaseTabName = tabName
      .split('-')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ')
      .replace(/The Pros/, 'The Pros'); // Fix special case

    

    // Find the tag key that matches this label
    const tagKey = Object.keys(constants.TAGS).find(
      key => constants.TAGS[key].label === titleCaseTabName
    );

    if (tagKey) {
      
      return tagKey;
    }

    // If we didn't find a matching tag, try the key directly
    if (constants.TAGS[tabName]) {
      
      return tabName;
    }

    // Last resort: try a case-insensitive match
    const caseInsensitiveKey = Object.keys(constants.TAGS).find(
      key => constants.TAGS[key].label.toUpperCase() === tabName
    );

    if (caseInsensitiveKey) {
      
      return caseInsensitiveKey;
    }

    // If all else fails, return the original name as a fallback
    
    return tabName;
  };

  // Updated fetchGamesForPortal function with cursor-based pagination
  const fetchGamesForPortal = async (limitCount = 10, tag = null, filterType = null, isInitialLoad = true, difficultyOverride = null) => {
    try {
      // Use the override if provided, otherwise use state
      const difficultyToUse = difficultyOverride || selectedDifficulty;
      
      // Use different loading states for initial load vs. loading more
      if (isInitialLoad) {
        setIsLoadingGames(true);
      } else {
        setIsLoadingMore(true);
      }

      // For special tabs, handle them separately
      if (filterType === 'TRENDING') {
        // Handle trending tab pagination
        await handleTrendingTabFetch(limitCount, isInitialLoad, difficultyToUse);
        return;
      }

      if (filterType === 'FOLLOWING') {
        // Handle following tab pagination
        await handleFollowingTabFetch(limitCount, isInitialLoad, difficultyToUse);
        return;
      }

      // For regular tag-based fetches, use the enhanced API
      const cursor = isInitialLoad ? null : nextCursor;

      

      // Use our enhanced API with cursor pagination
      const response = await fetchGames(
        limitCount * 2, // Fetch more games to have enough after filtering
        tag,
        difficultyToUse,
        null, // No creator filter
        cursor
      );

      // Check for errors
      if (response.error) {
        console.error("Error fetching games:", response.error);
        setHasMore(false);
        setIsLoadingGames(false);
        setIsLoadingMore(false);
        return;
      }

      const allGames = response.games;
      

      const uniqueGames = allGames;

      

      // Only filter for duplicates during pagination (not initial loads)
      let gamesToDisplay;
      if (isInitialLoad) {
        // On initial load, show all games without filtering
        gamesToDisplay = uniqueGames;
      } else {
        // During pagination, filter out games already on the page
        const currentGameIds = new Set(games.map(game => game.id));
        gamesToDisplay = uniqueGames.filter(game => !currentGameIds.has(game.id));
      }



      // Update the games state and pagination info
      if (isInitialLoad) {
        setGames(gamesToDisplay);
      } else {
        setGames(prevGames => [...prevGames, ...gamesToDisplay]);
      }

      setNextCursor(response.nextCursor);

      setHasMore(response.hasMore);

      

    } catch (error) {
      console.error("Error in fetchGamesForPortal:", error);
      setHasMore(false);
    } finally {
      setIsLoadingGames(false);
      setIsLoadingMore(false);
    }
  };

  // Special handler for trending tab
  const handleTrendingTabFetch = async (limitCount, isInitialLoad, difficultyToUse) => {
    try {
      // Add DETAILED logging for debugging
      
      
      
      // Clear games immediately on initial load to ensure a clean state
      if (isInitialLoad) {
        
        setGames([]);
        setIsLoadingGames(true);
      } else {
        setIsLoadingMore(true);
      }
      
      // Calculate timestamp for 7 days ago
      const NOW = new Date();
      const DAYS_7 = 7 * 24 * 60 * 60 * 1000;
      const TIMESTAMP_7_DAYS_AGO = NOW.getTime() - DAYS_7; // Use numeric timestamp
      
      
      
      
      // IMMEDIATE FETCH - For difficulty changes and initial load, try to get at least one game immediately
      let immediateFetchGames = []; // Store immediate fetch results for possible use later
      
      if (isInitialLoad) {
        try {
          
          
          // Simple query to get recent games
          const immediateQuery = query(
            collection(firestore, 'games'),
            orderBy('createdAt', 'desc'),
            limit(50) // Fetch more to ensure we find games matching the difficulty
          );
          
          const immediateSnapshot = await getDocs(immediateQuery);
          if (!immediateSnapshot.empty) {
            
            
            // Filter by timestamp and difficulty
            const filteredGames = [];
            const allTimeWindowGames = []; // Store all games in time window regardless of difficulty
            
            immediateSnapshot.docs.forEach(doc => {
              try {
                const gameData = doc.data();
                
                // First check if it's in the time window
                if (gameData.createdAt <= TIMESTAMP_7_DAYS_AGO) {
                  return; // Skip games outside time window
                }
                
                // Parse moves once for all games
                let parsedMoves = [];
                if (gameData.moves) {
                  try {
                    parsedMoves = JSON.parse(gameData.moves);
                  } catch (parseErr) {
                    console.error(`Error parsing moves for immediate game ${doc.id}:`, parseErr);
                  }
                }
                
                // Create a complete game object
                const game = {
                  id: doc.id,
                  ...gameData,
                  moves: parsedMoves,
                  title: gameData.title || "",
                  imageUrl: gameData.imageUrl || "",
                  difficulty: gameData.difficulty || "Beginner",
                  tags: Array.isArray(gameData.tags) ? gameData.tags : [],
                  creatorHandle: gameData.creatorHandle || "unknown"
                };
                
                // Add to all games in time window
                allTimeWindowGames.push(game);
                
                // Then check difficulty if needed
                if (difficultyToUse !== 'All Levels') {
                  const gameDifficulty = (gameData.difficulty || '').toLowerCase();
                  const filterDifficulty = difficultyToUse.toLowerCase();
                  
                  if (gameDifficulty !== filterDifficulty) {
                    
                    return; // Skip games that don't match difficulty
                  }
                }
                
                // Game matches criteria - log it
                
                
                filteredGames.push(game);
              } catch (gameErr) {
                console.error(`Error processing game ${doc.id}:`, gameErr);
              }
            });
            
            // Sort by score (highest first)
            filteredGames.sort((a, b) => (b.score || 0) - (a.score || 0));
            allTimeWindowGames.sort((a, b) => (b.score || 0) - (a.score || 0));
            
            // Store the immediate fetch results for possible use later
            immediateFetchGames = difficultyToUse === 'All Levels' ? allTimeWindowGames : filteredGames;
            
            if (filteredGames.length > 0) {
              
              // filteredGames.slice(0, 3).forEach((game, i) => 
              //   console.log(`  ${i+1}. Game ${game.id}: difficulty="${game.difficulty}", score=${game.score || 0}`)
              // );
              
              // For All Levels, use the full set of games in the time window
              const gamesToSet = difficultyToUse === 'All Levels' ? allTimeWindowGames : filteredGames;
              
              // Always display ALL matching games to avoid missing content
              setGames(gamesToSet);
              
            } else {
              
            }
          }
        } catch (err) {
          console.error("Error in immediate game fetch:", err);
        }
      }

      // For initial loads we fetch more to ensure enough after filtering
      const effectiveLimit = isInitialLoad ? limitCount * 2 : limitCount;

      // STEP 1: Check if ANY games exist in the database (diagnostic query)
      
      const diagnosticQuery = query(
        collection(firestore, 'games'),
        orderBy('createdAt', 'desc'),
        limit(3)
      );
      
      const diagnosticSnapshot = await getDocs(diagnosticQuery);
      if (diagnosticSnapshot.empty) {
        
      } else {
        
        diagnosticSnapshot.forEach(doc => {
          const gameData = doc.data();
          
          
          // Check if this game would be in the trending time window
          const isInTimeWindow = gameData.createdAt > TIMESTAMP_7_DAYS_AGO;
          
        });
      }

      // STEP 2: Try querying ONLY with createdAt filter (no ordering by score)
      
      let simplifiedQuery = query(
        collection(firestore, 'games'),
        where('createdAt', '>', TIMESTAMP_7_DAYS_AGO),
        orderBy('createdAt', 'desc'),
        limit(effectiveLimit)
      );
      
      try {
        const simplifiedSnapshot = await getDocs(simplifiedQuery);
        
        
        if (!simplifiedSnapshot.empty) {
          
          simplifiedSnapshot.docs.slice(0, 2).forEach(doc => {
            const game = doc.data();
            
          });
        }
      } catch (simplifiedErr) {
        console.error("ATTEMPT 1: Error with simplified query:", simplifiedErr.message);
      }

      // STEP 3: Try querying with score only (no time filter)
      
      let scoreOnlyQuery = query(
        collection(firestore, 'games'),
        orderBy('score', 'desc'),
        limit(effectiveLimit)
      );
      
      try {
        const scoreSnapshot = await getDocs(scoreOnlyQuery);
        
        
        if (!scoreSnapshot.empty) {
          
          scoreSnapshot.docs.slice(0, 2).forEach(doc => {
            const game = doc.data();
            
          });
        }
      } catch (scoreErr) {
        console.error("ATTEMPT 2: Error with score-only query:", scoreErr.message);
      }

      // STEP 4: Try the full trending query (time filter + both ordering clauses)
      

      // Reference to the cursor document
      let cursorDoc;
      if (nextCursor) {
        try {
          
          const cursorRef = doc(firestore, 'games', nextCursor);
          cursorDoc = await getDoc(cursorRef);
          if (!cursorDoc.exists()) {
            console.error("Cursor document doesn't exist");
            cursorDoc = null;
          }
        } catch (err) {
          console.error("Error retrieving cursor document:", err);
          cursorDoc = null;
        }
      }

      // Build full query for trending games
      let fullQuery;
      try {
        fullQuery = query(
        collection(firestore, 'games'),
        where('createdAt', '>', TIMESTAMP_7_DAYS_AGO),
        orderBy('createdAt', 'desc'),
        orderBy('score', 'desc')
      );

      // Apply pagination if we have a cursor
      if (cursorDoc) {
          fullQuery = query(fullQuery, startAfter(cursorDoc));
      }

      // Apply limit
        fullQuery = query(fullQuery, limit(effectiveLimit));
      } catch (queryConstructionErr) {
        console.error("Error constructing full query:", queryConstructionErr);
      }

      // Try to execute the full query
      let snapshot;
      try {
        
        snapshot = await getDocs(fullQuery);
        
        
        // If the full query returns no results but we know games exist,
        // it might be due to the index still building or a subtle query issue
        if (snapshot.empty && diagnosticSnapshot.size > 0) {
          
          
          // ALTERNATIVE APPROACH: Fetch recent games and sort by score in memory
          
          const alternativeQuery = query(
            collection(firestore, 'games'),
            orderBy('createdAt', 'desc'),
            limit(100) // Fetch MORE to ensure we get all matching games
          );
          
          const altSnapshot = await getDocs(alternativeQuery);
          
          
          if (!altSnapshot.empty) {
            // Process these results and filter to last 7 days
            const now = Date.now();
            const filteredDocs = altSnapshot.docs.filter(doc => {
              const data = doc.data();
              return data.createdAt > TIMESTAMP_7_DAYS_AGO;
            });
            
            
            
            // Log the difficulty of every game for debugging
            
            filteredDocs.forEach(doc => {
              const game = doc.data();
              
            });
            
            // Sort by score descendingly (highest scores first)
            filteredDocs.sort((docA, docB) => {
              const scoreA = docA.data().score || 0;
              const scoreB = docB.data().score || 0;
              return scoreB - scoreA; // Descending order
            });
            
            
            filteredDocs.slice(0, 3).forEach(doc => {
              const game = doc.data();
              
            });
            
            // Create a custom snapshot-like object
            snapshot = {
              docs: filteredDocs,
              empty: filteredDocs.length === 0,
              size: filteredDocs.length
            };
          }
        }
      } catch (fullQueryErr) {
        console.error("Error executing full trending query:", fullQueryErr);
        
        
        
        
        
        // Fall back to simplified query for results
        
        snapshot = await getDocs(simplifiedQuery);
      }

      // STEP 5: Process results from whichever query succeeded
      if (snapshot.empty) {
        
        setHasMore(false);
        if (isInitialLoad) setGames([]);
        setIsLoadingGames(false);
        setIsLoadingMore(false);
        
        return;
      }

      // Convert to array of game data
      const allGames = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      
      
      // Log the first few games for debugging
      if (allGames.length > 0) {
        
        allGames.slice(0, 3).forEach(game => {
          
        });
      }

      // Apply difficulty filter if needed
      let filteredGames = allGames;
      if (difficultyToUse && difficultyToUse !== "All Levels") {
        // Case-insensitive difficulty check to be safer
        const difficultyLower = difficultyToUse.toLowerCase();
        
        
        
        
        // Extra logging of all games and their difficulties
        
        filteredGames.forEach(game => {
          
        });
        
        const beforeCount = filteredGames.length;
        
        // FIX: Better case handling for difficulty matching
        filteredGames = allGames.filter(game => {
          const gameDifficulty = (game.difficulty || "").toLowerCase();
          const matches = gameDifficulty === difficultyLower;
          
          // Log important debugging info
          
          
          if (!matches) {
            
          }
          return matches;
        });
        
        
        
        // CRITICAL FIX: If no games found after filtering but we had games in immediate fetch, use them
        if (filteredGames.length === 0 && isInitialLoad && immediateFetchGames && immediateFetchGames.length > 0) {
          
          filteredGames = immediateFetchGames;
          
        }
      } else {
        
        
        // IMPORTANT FIX: For All Levels, if immediate fetch found more games than main query, 
        // use the immediate fetch results which are more complete
        if (isInitialLoad && immediateFetchGames && immediateFetchGames.length > filteredGames.length) {
          
          filteredGames = immediateFetchGames;
          
        }
      }

      // TRENDING tab skips "already seen" filtering, just like the FOLLOWING tab
      // This ensures users always see the most trending content regardless of history
      
      
      // Only filter for duplicates during pagination (not initial loads)
      let finalGamesToDisplay;
      if (isInitialLoad) {
        // On initial load, show ALL games matching criteria without filtering
        finalGamesToDisplay = filteredGames;
        
        
        // CRITICAL FIX: If no games after filtering but we were in initial load, 
        // something went wrong - force a re-query
        if (finalGamesToDisplay.length === 0 && difficultyToUse !== 'All Levels') {
          
          
          // Try a direct query with the exact difficulty to fix the issue
          try {
            const recoveryQuery = query(
              collection(firestore, 'games'),
              orderBy('createdAt', 'desc'),
              limit(20)
            );
            
            
            getDocs(recoveryQuery).then(recoverySnapshot => {
              if (!recoverySnapshot.empty) {
                const recoveryGames = [];
                
                recoverySnapshot.docs.forEach(doc => {
                  try {
                    const gameData = doc.data();
                    
                    // First check if it's in the time window
                    if (gameData.createdAt <= TIMESTAMP_7_DAYS_AGO) {
                      return; // Skip games outside time window
                    }
                    
                    // Check for exact difficulty match
                    if (difficultyToUse !== 'All Levels') {
                      const gameDifficulty = (gameData.difficulty || '').toLowerCase();
                      const filterDifficulty = difficultyToUse.toLowerCase();
                      
                      
                      if (gameDifficulty !== filterDifficulty) {
                        return; // Skip games that don't match difficulty
                      }
                    }
                    
                    // Parse moves
                    let parsedMoves = [];
                    if (gameData.moves) {
                      try {
                        parsedMoves = JSON.parse(gameData.moves);
                      } catch (parseErr) {
                        console.error(`Error parsing moves for recovery game ${doc.id}:`, parseErr);
                      }
                    }
                    
                    // Create the game object
                    const game = {
                      id: doc.id,
                      ...gameData,
                      moves: parsedMoves,
                      title: gameData.title || "",
                      imageUrl: gameData.imageUrl || "",
                      difficulty: gameData.difficulty || "Beginner",
                      tags: Array.isArray(gameData.tags) ? gameData.tags : [],
                      creatorHandle: gameData.creatorHandle || "unknown"
                    };
                    
                    recoveryGames.push(game);
                  } catch (gameErr) {
                    console.error(`Error processing recovery game ${doc.id}:`, gameErr);
                  }
                });
                
                if (recoveryGames.length > 0) {
                  
                  setGames(recoveryGames);
                }
              }
            }).catch(err => {
              console.error("Recovery query failed:", err);
            });
          } catch (err) {
            console.error("Failed to execute recovery query:", err);
          }
        }
      } else {
        // For pagination, filter out games already on the page
        const currentGameIds = new Set(games.map(game => game.id));
        finalGamesToDisplay = filteredGames.filter(game => !currentGameIds.has(game.id));
        
      }
      
      // Force immediate update of games state to ensure content is visible right away
      if (isInitialLoad) {
        
        // Always show all matching games on initial load
        setGames(finalGamesToDisplay);
      } else {
        // For pagination, append to existing games
        
        setGames(prevGames => [...prevGames, ...finalGamesToDisplay]);
      }

      // Set the cursor for next page
      if (snapshot.docs.length > 0) {
        setNextCursor(snapshot.docs[snapshot.docs.length - 1].id);
      } else {
        setNextCursor(null);
      }

      const receivedFullBatch = allGames.length >= effectiveLimit;
      setHasMore(receivedFullBatch);

      
      
    } catch (error) {
      console.error("ERROR in handleTrendingTabFetch:", error);
      // Show the error to help debugging
      alert(`Error in trending tab: ${error.message}`);
      setHasMore(false);
      setIsLoadingGames(false);
      setIsLoadingMore(false);
    }
  };

  // Helper function to get users the current user follows
  const getFollowedUsers = async (userHandle) => {
    try {
      const userDoc = await getDocs(
        query(collection(firestore, 'users'), where('handle', '==', userHandle))
      );
      
      if (userDoc.empty) return [];
      
      const userData = userDoc.docs[0].data();
      return userData.following || [];
    } catch (error) {
      console.error("Error fetching followed users:", error);
      return [];
    }
    
  };

  // Special handler for following tab
  // Unlike other tabs, FOLLOWING never filters out games the user has already seen
  // This ensures users always see all games from accounts they follow
  const handleFollowingTabFetch = async (limitCount, isInitialLoad, difficultyToUse) => {
    try {
      // Clear games immediately on initial load to ensure a clean state
      if (isInitialLoad) {
        
        setGames([]);
        setIsLoadingGames(true);
      } else {
        setIsLoadingMore(true);
      }

      // Check if user is authenticated
    if (!props?.user?.credentials?.handle) {
        
      setGames([]);
      setHasMore(false);
      setIsLoadingGames(false);
      setIsLoadingMore(false);
      return;
    }

      

      // Get users the current user follows
      const followedUsers = await getFollowedUsers(props.user.credentials.handle);
      
      
      if (followedUsers.length === 0) {
        
        setGames([]);
        setHasMore(false);
    setIsLoadingGames(false);
    setIsLoadingMore(false);
        return;
      }

      // IMMEDIATE FETCH - For initial load, try to get at least one game immediately
      // This solves the issue of games not showing up on first page load
      if (isInitialLoad && followedUsers.length > 0) {
        try {
          
          
          
          // Try each followed user in sequence until we find a game
          let foundGame = false;
          
          for (const followedUser of followedUsers) {
            if (foundGame) break;
            
            
            
            // Base query - no difficulty filter initially
            let immediateQuery = query(
              collection(firestore, 'games'),
              where('creatorHandle', '==', followedUser),
              orderBy('createdAt', 'desc')
            );
            
            // Fetch more games if we need to apply a difficulty filter client-side
            const fetchLimit = difficultyToUse !== 'All Levels' ? 10 : 1;
            immediateQuery = query(immediateQuery, limit(fetchLimit));
            
            const immediateSnapshot = await getDocs(immediateQuery);
            if (!immediateSnapshot.empty) {
              // Process games and apply difficulty filter if needed
              const games = [];
              
              immediateSnapshot.docs.forEach(doc => {
                try {
                  const gameData = doc.data();
                  
                  // Check difficulty filter first (for efficiency)
                  if (difficultyToUse !== 'All Levels') {
                    const gameDifficulty = (gameData.difficulty || '').toLowerCase();
                    const filterDifficulty = difficultyToUse.toLowerCase();
                    
                    if (gameDifficulty !== filterDifficulty) {
                      
                      return; // Skip this game
                    }
                  }
                  
                  // Parse moves
                  let parsedMoves = [];
                  if (gameData.moves) {
                    try {
                      parsedMoves = JSON.parse(gameData.moves);
                    } catch (parseErr) {
                      console.error(`Error parsing moves for immediate game ${doc.id}:`, parseErr);
                    }
                  }
                  
                  // Create the game object
                  const game = {
                    id: doc.id,
                    ...gameData,
                    moves: parsedMoves,
                    title: gameData.title || "",
                    imageUrl: gameData.imageUrl || "",
                    difficulty: gameData.difficulty || "Beginner",
                    tags: Array.isArray(gameData.tags) ? gameData.tags : [],
                    creatorHandle: gameData.creatorHandle || "unknown"
                  };
                  
                  games.push(game);
                } catch (gameErr) {
                  console.error(`Error processing game ${doc.id}:`, gameErr);
                }
              });
              
              if (games.length > 0) {
                const firstGame = games[0];
                
                
                // Set this game immediately to ensure something shows on first page load
                setGames([firstGame]);
                foundGame = true;
              } else {
                
              }
            } else {
              
            }
          }
          
          if (!foundGame) {
            
          }
        } catch (err) {
          console.error("Error in immediate game fetch:", err);
        }
      }

      // DEBUGGING: Check if we can find ANY games from these users directly
      
      const debugQuery = query(
        collection(firestore, 'games'),
        orderBy('createdAt', 'desc'),
        limit(100)
      );
      
      try {
        const debugSnapshot = await getDocs(debugQuery);
        
        
        // Create a map of creator handles to their games
        const creatorGamesMap = {};
        
        debugSnapshot.docs.forEach(doc => {
          const game = doc.data();
          const creatorHandle = game.creatorHandle;
          
          if (!creatorGamesMap[creatorHandle]) {
            creatorGamesMap[creatorHandle] = [];
          }
          
          creatorGamesMap[creatorHandle].push({
            id: doc.id,
            createdAt: game.createdAt,
            title: game.title || "[No Title]"
          });
        });
        
        // Log games for each followed user
        followedUsers.forEach(handle => {
          const gamesForUser = creatorGamesMap[handle] || [];
          
          if (gamesForUser.length > 0) {
            gamesForUser.forEach(game => {
              
            });
          } else {
            
          }
        });
        
        // Check for case sensitivity issues
        
        const allCreatorHandles = Object.keys(creatorGamesMap);
        followedUsers.forEach(handle => {
          const exactMatch = allCreatorHandles.includes(handle);
          const lowerCaseMatches = allCreatorHandles.filter(h => h.toLowerCase() === handle.toLowerCase());
          
          if (!exactMatch && lowerCaseMatches.length > 0) {
            
          }
        });
      } catch (err) {
        console.error("🔍 DEBUG: Error in debug query:", err);
      }

      // Retrieve games by the followed users
      const gamesResults = [];
      const batchSize = 10; // Firestore allows max 10 items in "in" clause
      
      // Split followed users into batches of 10
      for (let i = 0; i < followedUsers.length; i += batchSize) {
        const batch = followedUsers.slice(i, i + batchSize);
        
        
        let gamesQuery = query(
          collection(firestore, 'games'),
          where('creatorHandle', 'in', batch),
          orderBy('createdAt', 'desc')
        );
        
        // Apply cursor if not initial load
        if (!isInitialLoad && nextCursor) {
          try {
            
            const cursorRef = doc(firestore, 'games', nextCursor);
            const cursorDoc = await getDoc(cursorRef);
            if (cursorDoc.exists()) {
              gamesQuery = query(gamesQuery, startAfter(cursorDoc));
            } else {
              
            }
          } catch (err) {
            console.error("Error with cursor:", err);
          }
        }
        
        // Apply limit
        gamesQuery = query(gamesQuery, limit(limitCount * 2));
        
        try {
          const snapshot = await getDocs(gamesQuery);
          const batchGames = [];
          
          // Careful handling of each game document to ensure it's processed correctly
          snapshot.docs.forEach(doc => {
            try {
              const gameData = doc.data();
              
              // Ensure moves is properly parsed or initialized as empty array
              let parsedMoves = [];
              if (gameData.moves) {
                try {
                  parsedMoves = JSON.parse(gameData.moves);
                } catch (parseErr) {
                  console.error(`Error parsing moves for game ${doc.id}:`, parseErr);
                  // If it fails to parse, we'll use empty array
                }
              }
              
              // Create a complete game object with all necessary fields
              const game = {
                id: doc.id,
                ...gameData,
                moves: parsedMoves, // Use the parsed moves
                // Ensure critical fields have default values if missing
                title: gameData.title || "",
                imageUrl: gameData.imageUrl || "",
                difficulty: gameData.difficulty || "Beginner",
                tags: Array.isArray(gameData.tags) ? gameData.tags : [],
                creatorHandle: gameData.creatorHandle || "unknown"
              };
              
              batchGames.push(game);
            } catch (gameErr) {
              console.error(`Error processing game ${doc.id}:`, gameErr);
            }
          });
          
          
          
          if (batchGames.length > 0) {
            
            batchGames.slice(0, 3).forEach(game => {
              
            });
          } else {
            // console.log("No games found in this batch. Query details:", {
            //   creatorHandles: batch,
            //   orderBy: "createdAt desc",
            //   limit: limitCount * 2
            // });
          }
          
          gamesResults.push(...batchGames);
        } catch (err) {
          console.error("Error querying games for batch:", err);
          
          // Check if this is an index error
          if (err.message && err.message.includes("index")) {
            console.error("This appears to be an index error. Please create the following index in Firebase:");
            console.error("Collection: games, Fields: creatorHandle ASC, createdAt DESC");
          }
        }
      }
      
      // Sort by creation date (most recent first)
      gamesResults.sort((a, b) => b.createdAt - a.createdAt);
      
      
      // Log the games we found before any filtering
      
      
      // Don't limit to limitCount*2 here, this was cutting off valid games
      // Let the pagination system handle load-more properly
      const allGames = gamesResults;
      
      // Apply difficulty filter if needed
      let filteredGames = allGames;
      if (difficultyToUse && difficultyToUse !== "All Levels") {
        // Case-insensitive difficulty check to be safer
        const difficultyLower = difficultyToUse.toLowerCase();
        filteredGames = allGames.filter(game => {
          const gameDifficulty = (game.difficulty || "").toLowerCase();
          const matches = gameDifficulty === difficultyLower;
          if (!matches) {
            
          }
          return matches;
        });
        
      }
      
      // Only filter for duplicates during pagination (not initial loads)
      let pageGames;
      if (isInitialLoad) {
        // On initial load, show games without filtering, take first batch
        pageGames = filteredGames.slice(0, limitCount);
      } else {
        // For pagination, filter out games already on the page
        const currentGameIds = new Set(games.map(game => game.id));
        const gamesToDisplay = filteredGames.filter(game => !currentGameIds.has(game.id));
        pageGames = gamesToDisplay.slice(0, limitCount);
      }
      
      
      
      pageGames.forEach(game => {
        
      });
      
      // Set next cursor if we have more games
      if (filteredGames.length > 0) {
        // Set cursor to last game we've looked at
        const lastIndex = isInitialLoad 
          ? Math.min(limitCount - 1, filteredGames.length - 1)
          : Math.min(2 * limitCount - 1, filteredGames.length - 1);
          
        if (lastIndex >= 0) {
          setNextCursor(filteredGames[lastIndex].id);
          
        } else {
          setNextCursor(null);
        }
      } else {
        setNextCursor(null);
      }
      
      // For FOLLOWING tab, we don't track seen games
      // This ensures all games are always visible when returning to the tab
      
      // Update games state
      if (isInitialLoad && pageGames.length > 0) {
        // For initial load, replace any immediate fetch with the full fetch results
        
        setGames(pageGames);
      } else if (!isInitialLoad) {
        // For pagination, append to existing games
        setGames(prevGames => [...prevGames, ...pageGames]);
      }
      
      // Set hasMore flag - FIX: correctly determine if there are more games
      // There are more games if we found more than we're displaying in this batch
      const remainingGames = filteredGames.length - (isInitialLoad ? limitCount : games.length + limitCount);
      const hasMoreGames = remainingGames > 0;
      
      
      setHasMore(hasMoreGames);
      
    } catch (error) {
      console.error("Error in handleFollowingTabFetch:", error);
      setHasMore(false);
    } finally {
      setIsLoadingGames(false);
      setIsLoadingMore(false);
    }
  };

  // Helper function to load games based on the current state
  const loadGamesWithFilters = () => {
    // Reset pagination state
    setNextCursor(null);
    setHasMore(true);

    // For special tabs, pass the tab name as filterType
    const specialTabs = ['NEW', 'FOLLOWING', 'TRENDING'];

    if (specialTabs.includes(activeTab)) {
      fetchGamesForPortal(10, null, activeTab, true, selectedDifficulty);
    } else {
      // For tag tabs, get the tag key and pass it
      const tagKey = getTagKeyFromTabName(activeTab);
      fetchGamesForPortal(10, tagKey, null, true, selectedDifficulty);
    }
  };

  // Check for activeTag in location state when component mounts
  useEffect(() => {
    // console.log('Portal useEffect triggered - location.state:', location.state);
    // console.log('Portal useEffect triggered - location.pathname:', location.pathname);
    // console.log('Portal useEffect triggered - current activeTab:', activeTab);
    
    if (location.state) {
      // Check if we need to open the openings modal
      if (location.state.openOpeningsModal && location.state.activeTab === 'OPENINGS') {
        setActiveTab('OPENINGS');
        setShowOpeningsDropdown(true); // Open the modal
        // Fetch initial games for the OPENINGS tab (e.g., all openings)
        fetchAllOpeningsGames(25);
        // Clear the state to prevent re-opening on refresh
        window.history.replaceState({}, document.title);
        return;
      }

      // Check if we have opening information to filter by (existing logic)
      if (location.state.activeTag === 'OPENINGS' && location.state.selectedOpening) {
        // Set the active tab to OPENINGS
        setActiveTab('OPENINGS');

        // Set the selected opening
        setSelectedOpening(location.state.selectedOpening);
        setIsOpeningSelected(true);

        // Create the combined tag
        const combinedTag = `Openings - ${location.state.selectedOpening}`;

        // Fetch games with this opening tag
        fetchGamesForPortal(10, combinedTag, null);

        // Clear the location state to prevent re-applying on refresh
        window.history.replaceState({}, document.title);
        return;
      }
      // Check for regular tag navigation
      else if (location.state.activeTag) {
        // console.log('Portal: Received activeTag from location.state:', location.state.activeTag);
        // console.log('Portal: Received filterTag from location.state:', location.state.filterTag);
        
        // Set the active tab to the one from navigation state
        setActiveTab(location.state.activeTag);

        // Check if we have a separate filterTag (for cases where case sensitivity matters)
        const tagToFilter = location.state.filterTag || location.state.activeTag;
        // console.log('Portal: tagToFilter:', tagToFilter);

        // Find the tag key that matches this label
        const tagKey = Object.keys(constants.TAGS).find(
          key => constants.TAGS[key].label === tagToFilter
        );
        // console.log('Portal: Found tagKey:', tagKey);
        // console.log('Portal: Available tag keys:', Object.keys(constants.TAGS));

        // Fetch games with this tag
        if (tagKey) {
          // console.log('Portal: Fetching games with tagKey:', tagKey);
          fetchGamesForPortal(10, tagKey, null);
        } else {
          // If we can't find a matching tag key, use the label directly
          // console.log('Portal: No tagKey found, using tagToFilter directly:', tagToFilter);
          fetchGamesForPortal(10, tagToFilter, null);
        }

        // Clear the location state to prevent re-applying on refresh
        window.history.replaceState({}, document.title);
        return;
      }
    }

    // Initial fetch of games when no specific state is passed
    loadGamesWithFilters();
  }, [location]); // Keep dependencies as is, but the logic inside handles the new state

  // Handler for difficulty selection
  const handleDifficultyChange = (difficulty) => {
    

    // Store in localStorage first
    localStorage.setItem('portalDifficulty', difficulty);
    setShowDifficultyDropdown(false);

    // Clear games and reset pagination to ensure fresh content
    setGames([]);
    setNextCursor(null);
    setHasMore(true);
    setIsLoadingGames(true);
    
    // Special handling for TRENDING tab to ensure complete tracking reset
    if (activeTab === 'TRENDING') {
      
    }
    // Handle FOLLOWING tab similarly
    else if (activeTab === 'FOLLOWING') {
      
    }

    // Don't rely on state update before loading games
    // Instead, fetch games with the new difficulty directly
    const specialTabs = ['NEW', 'FOLLOWING', 'TRENDING'];

    if (specialTabs.includes(activeTab)) {
      fetchGamesForPortal(10, null, activeTab, true, difficulty);
    } else {
      // For tag tabs, get the tag key and pass it
      const tagKey = getTagKeyFromTabName(activeTab);
      fetchGamesForPortal(10, tagKey, null, true, difficulty);
    }

    // Update state after initiating the fetch
    setSelectedDifficulty(difficulty);
  };

  // Helper function to get the display name for a tab (for handling special case of openings)
  const getTabDisplayName = (tab) => {
    if (tab === 'OPENINGS' && selectedOpening && isOpeningSelected) {
      return `OPENINGS - ${selectedOpening.toUpperCase()}`;
    }
    return tab;
  };

  // Effect to close openings dropdown when clicking outside
  React.useEffect(() => {
    // const handleClickOutside = (event) => {
    //   if (openingsDropdownRef.current && !openingsDropdownRef.current.contains(event.target)) {
    //     setShowOpeningsDropdown(false);
    //   }
    // };

    // Add event listener
    // document.addEventListener('mousedown', handleClickOutside);

    // // Cleanup
    // return () => {
    //   document.removeEventListener('mousedown', handleClickOutside);
    // };
  }, []);

  // Handler for opening selection
  const handleOpeningSelect = (opening) => {
    if (opening === 'ALL OPENINGS') {
      setSelectedOpening(null);
      setIsOpeningSelected(true);
      setShowOpeningsDropdown(false);

      // Load all games with any Openings-related tag
      // We'll fetch a larger set of games and filter them in the UI
      setActiveTab('OPENINGS');

      // Fetch more games than usual
      fetchAllOpeningsGames(25);
    } else {
      setSelectedOpening(opening);
      setIsOpeningSelected(true);
      setShowOpeningsDropdown(false);

      // Create the combined tag
      const combinedTag = `Openings - ${opening}`;
      fetchGamesForPortal(10, combinedTag, null);
    }

    // Update the active tab
    setActiveTab('OPENINGS');
  };

  // New function to fetch all openings games
  const fetchAllOpeningsGames = async (limitCount = 25) => {
    try {
      
      setIsLoadingGames(true);

      // Get all games from the database (we'll filter client-side)
      const allGamesQuery = query(
        collection(firestore, "games"),
        orderBy("createdAt", "desc"),
        limit(100) // Get a larger set to filter from
      );

      const gamesSnap = await getDocs(allGamesQuery);
      const allGames = [];

      gamesSnap.forEach(doc => {
        allGames.push({
          id: doc.id,
          ...doc.data()
        });
      });

      

      // Filter games to only include those with opening-related tags
      const openingsGames = allGames.filter(game => {
        if (!game.tags || !Array.isArray(game.tags)) return false;

        // Include any game that has a tag starting with "Openings"
        return game.tags.some(tag =>
          typeof tag === 'string' && (
            tag === 'Openings' ||
            tag.startsWith('Openings -') ||
            tag.startsWith('Openings-')
          )
        );
      });

      

      // Apply difficulty filter if needed
      let filteredGames = openingsGames;
      if (selectedDifficulty !== 'All Levels') {
        filteredGames = openingsGames.filter(game =>
          game.difficulty === selectedDifficulty
        );
      }

      // Sort by creation date (newest first)
      filteredGames.sort((a, b) => {
        const dateA = a.createdAt || 0;
        const dateB = b.createdAt || 0;
        return dateB - dateA;
      });

      // Take the top games up to the limitCount
      const limitedGames = filteredGames.slice(0, limitCount);

      setGames(limitedGames);
      setIsLoadingGames(false);

    } catch (error) {
      console.error("Error fetching all openings games:", error);
      setGames([]);
      setIsLoadingGames(false);
    }
  };

  // Filter openings based on search term
  const getFilteredOpenings = () => {
    // Start with ALL OPENINGS option
    const filteredOpenings = ['ALL OPENINGS'];

    // Add filtered openings from allowedEcoGroups
    if (openingSearchTerm.trim() === '') {
      // If no search term, add all openings
      return filteredOpenings.concat(Object.keys(allowedEcoGroups));
    } else {
      // Filter openings based on search term
      const searchTermLower = openingSearchTerm.toLowerCase();
      const matchingOpenings = Object.keys(allowedEcoGroups).filter(
        opening => opening.toLowerCase().includes(searchTermLower)
      );
      return filteredOpenings.concat(matchingOpenings);
    }
  };

  // Update handleTabClick function
  const handleTabClick = (tab) => {
    // If changing to a different tab, reset the games list and pagination state
    if (tab !== activeTab) {
      
      
      // Reset states
      setGames([]);
      setNextCursor(null);
      setHasMore(true);
      setIsLoadingGames(true); // Set loading state immediately
      
      
      // Update the active tab
      setActiveTab(tab);

      // Special case for openings
      if (tab === 'OPENINGS') {
        setShowOpeningsDropdown(true);
        setIsLoadingGames(false);
        return; // Don't load games yet, wait for opening selection
      }

      // For special tabs, pass the tab name as filterType
      const specialTabs = ['NEW', 'FOLLOWING', 'TRENDING'];

      if (specialTabs.includes(tab)) {
        
        fetchGamesForPortal(10, null, tab, true, selectedDifficulty);
      } else {
        // For tag tabs, get the tag key and pass it
        const tagKey = getTagKeyFromTabName(tab);
        
        fetchGamesForPortal(10, tagKey, null, true, selectedDifficulty);
      }
    }
  };

  // Filter out invalid game objects
  const gameList = games.filter(game => Object.hasOwn(game, "id"));

  // Add these refs and effect at the component level
  const scrollContainerRef = React.useRef(null);
  const leftArrowRef = React.useRef(null);
  const rightArrowRef = React.useRef(null);
  const difficultyDropdownRef = React.useRef(null);
  const openingsDropdownRef = React.useRef(null);

  React.useEffect(() => {
    const container = scrollContainerRef.current;
    const leftArrow = leftArrowRef.current;
    const rightArrow = rightArrowRef.current;

    const checkScroll = () => {
      if (container && leftArrow && rightArrow) { // Check if all refs exist
        const hasHorizontalScroll = container.scrollWidth > container.clientWidth;
        if (leftArrow.style && rightArrow.style) { // Additional safety check
          leftArrow.style.display = hasHorizontalScroll ? 'block' : 'none';
          rightArrow.style.display = hasHorizontalScroll ? 'block' : 'none';
        }
      }
    };

    // Check on mount and window resize
    checkScroll();
    window.addEventListener('resize', checkScroll);

    // Cleanup
    return () => window.removeEventListener('resize', checkScroll);
  }, []); // Dependencies array remains empty

  // Add effect to close difficulty dropdown when clicking outside
  React.useEffect(() => {
    const handleClickOutside = (event) => {
      if (difficultyDropdownRef.current && !difficultyDropdownRef.current.contains(event.target)) {
        setShowDifficultyDropdown(false);
      }
    };

    // Add event listener
    document.addEventListener('mousedown', handleClickOutside);

    // Cleanup
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Function to create an array with PremiumSummary and PlaySummary components inserted
  const createGamesList = (games) => {
    const result = [];
    // Filter out invalid game objects **before** mapping IDs or rendering
    const validGames = games.filter(game => game && Object.hasOwn(game, "id"));

    // Show loading animation when games are being fetched
    if (isLoadingGames) {
      return [
        <div key="loading" style={{ flexShrink: 0, width: '100%', display: 'flex', justifyContent: 'center' }}>
          <div style={{
            color: 'gold',
            textAlign: 'center',
            padding: '20px',
            backgroundColor: '#222',
            borderRadius: '10px',
            width: '350px',
            height: '350px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <div>
              <div style={{ fontSize: '24px', marginBottom: '10px', display: 'flex', justifyContent: 'center' }}>
                <img
                  src={chessslogo}
                  alt="Loading chess"
                  style={{
                    width: '50px',
                    height: 'auto',
                    animation: 'spin 2s linear infinite'
                  }}
                />
              </div>
              <p>Loading games...</p>
              <style>
                {`
                  @keyframes spin {
                    0% { transform: rotate(0deg); }
                    100% { transform: rotate(360deg); }
                  }
                `}
              </style>
            </div>
          </div>
        </div>
      ];
    }

    // If no games found and we're not loading, show an appropriate message
    if (validGames.length === 0 && !isLoadingGames) {
      // Keep the FOLLOWING message separate as requested
      if (activeTab === 'FOLLOWING') {
        return [
          <div key="no-following" style={{ flexShrink: 0, width: '100%', display: 'flex', justifyContent: 'center' }}>
            <div style={{
              color: 'white',
              textAlign: 'center',
              padding: '20px',
              backgroundColor: '#222',
              borderRadius: '10px',
              width: '500px',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '15px',
              border: '3px solid gold'
            }}>
              <i className="fa-solid fa-user-friends" style={{ fontSize: '36px', color: 'gold' }}></i>
              <h3 style={{ margin: 0, color: 'gold' }}>No Games From Your Followed Users</h3>
              <p style={{ margin: 0 }}>The users you follow haven't posted any games yet,<br />or you're not following anyone yet.</p>
              <p style={{ margin: 0, fontSize: '14px', color: '#aaa' }}>Visit user profiles and click "Follow" to see their games here!</p>
              <button style={{ margin: 0, fontSize: '14px', color: 'gold', backgroundColor: 'transparent', border: '3px solid gold', borderRadius: '5px', padding: '5px 10px' }} onClick={() => {
                setActiveTab('NEW');
                setSelectedDifficulty('All Levels');
                // Don't call loadGamesWithFilters() here since state updates are asynchronous
                // Instead, directly call fetchGamesForPortal with the specific parameters we want
                fetchGamesForPortal(10, null, 'NEW');
                // Also update localStorage for persistence
                localStorage.setItem('portalDifficulty', 'All Levels');

              }}>
                Show Me New Games
              </button>
            </div>
          </div>
        ];
      }

      // For all other tabs (NEW, tag categories, etc.)
      else {
        // Determine the tab name for the message
        let tabName = activeTab;
        let tabColor = '#3498db'; // Default blue color
        let tabIcon = 'fa-chess-board';

        // For the NEW tab
        if (activeTab === 'NEW') {
          tabName = 'New Games';
          tabIcon = 'fa-chess';
        }
        // For tag tabs, format the name more nicely
        else if (activeTab !== 'NEW' && activeTab !== 'TRENDING') {
          // Keep the tab name as is, it's already properly formatted
          tabIcon = 'fa-tag';
        }

        // Show difficulty in the message if it's not "All Levels"
        const difficultyText = selectedDifficulty !== 'All Levels'
          ? ` with ${selectedDifficulty} Difficulty`
          : '';

        return [
          <div key="no-games" style={{ flexShrink: 0, width: '100%', display: 'flex', justifyContent: 'center' }}>
            <div style={{
              color: 'white',
              textAlign: 'center',
              padding: '20px',
              backgroundColor: '#222',
              borderRadius: '10px',
              width: '500px',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '15px',
              border: `3px solid gold`
            }}>
              <i className={`fa-solid fa-chess`} style={{ fontSize: '36px', color: 'gold' }}></i>
              <h3 style={{ margin: 0, color: 'gold' }}>No Games With These Filters:</h3>
              <p style={{ margin: 0, fontStyle: 'italic' }}>{tabName}{difficultyText}</p>
              <p style={{ margin: 0, fontSize: '14px', color: '#aaa' }}>Try changing the difficulty or the tag.<br />Or, be the first to upload a game with these parameter.</p>

              <button style={{ margin: 0, fontSize: '14px', color: 'gold', backgroundColor: 'transparent', border: '3px solid gold', borderRadius: '5px', padding: '5px 10px' }} onClick={() => {
                setActiveTab('NEW');
                setSelectedDifficulty('All Levels');
                // Don't call loadGamesWithFilters() here since state updates are asynchronous
                // Instead, directly call fetchGamesForPortal with the specific parameters we want
                fetchGamesForPortal(10, null, 'NEW');
                // Also update localStorage for persistence
                localStorage.setItem('portalDifficulty', 'All Levels');

              }}>
                Show Me New Games
              </button>
            </div>
          </div>
        ];
      }
    }

    // Add NewsSummary as the first component only when on the NEW tab
    if (activeTab === 'NEW') {
      result.push(
        <div key="news-summary" style={{ flexShrink: 0 }}>
          <div style={{
            border: '3px solid gold',
            borderRadius: '8px',
            padding: '10px',
            margin: '5px',
            display: 'flex',
            alignItems: 'stretch',
            boxSizing: 'border-box',
            width: '250px',
            backgroundColor: '#1a1a1a',
            height: '500px' // Set explicit minimum height
          }}>
            <NewsSummary
              style={{
                flex: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            />
          </div>
        </div>
      );
    }

    if (activeTab === 'Puzzle') {
      result.push(
        <div key="puzzle-summary" style={{ flexShrink: 0 }}>
          <div style={{
            border: '3px solid gold',
            borderRadius: '8px',
            padding: '10px',
            margin: '5px',
            display: 'flex',
            alignItems: 'stretch',
            boxSizing: 'border-box',
            width: '250px',
            backgroundColor: '#1a1a1a',
            height: '500px' // Set explicit minimum height
          }}>
            <PuzzleSummary
              style={{
                flex: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            />
          </div>
        </div>
      );
    }

    // Get the current selected tag (if not a special tab)
    const specialTabs = ['NEW', 'FOLLOWING', 'TRENDING'];
    let selectedTagKey = null;

    if (!specialTabs.includes(activeTab)) {
      // Find the tag key that matches the active tab label
      selectedTagKey = Object.keys(constants.TAGS).find(
        key => constants.TAGS[key].label === activeTab
      );

      // If we couldn't find it by label, use the activeTab directly
      if (!selectedTagKey) {
        selectedTagKey = activeTab;
      }
    }

    validGames.forEach((game, index) => {
      // Add the game summary
      result.push(
        <div key={game.id} style={{ flexShrink: 0 }}>
          <div style={{ width: '' }}>
            <GameSummary
              gameData={game}
              boardWidth={400}
              priorityTag={selectedTagKey} // Pass the selected tag as priority
              onClick={() => {
                if (activeTab === 'TRENDING') {
                  const trendingGameIds = validGames.map(g => g.id);

                  // Use history.push with state
                  history.push(`/games/${game.id}`, {
                    origin: 'TRENDING',
                    gameIds: trendingGameIds
                  });
                } else if (activeTab === 'FOLLOWING') {
                  const followingGameIds = validGames.map(g => g.id);

                  history.push(`/games/${game.id}`, {
                    origin: 'FOLLOWING',
                    gameIds: followingGameIds
                  });
                } else if (!specialTabs.includes(activeTab)) {
                  // For any tag filter (including Openings), pass the gameIds array to maintain navigation state
                  const tagFilteredGameIds = validGames.map(g => g.id);

                  // Include both the tag in URL params and the full gameIds array in state
                  const queryParams = selectedTagKey ? `?tag=${selectedTagKey}` : '';

                  // Use history.push with both URL params and state
                  history.push(`/games/${game.id}${queryParams}`, {
                    origin: activeTab,
                    gameIds: tagFilteredGameIds
                  });
                } else {
                  // For NEW tab or any other case
                  history.push(`/games/${game.id}`);
                }
              }}
            />
          </div>
        </div>
      );

      // After every third game, add a PremiumSummary if user is NOT premium
      // Changed showPremiumContent to !premiumFeatures.hideAdsAndUpsells
      // COMMENTED OUT: Premium modal insertion in timeline
      /*
      if ((index + 1) % 8 === 0 && !premiumFeatures.hideAdsAndUpsells) {
        result.push(
          <div key={`premium-${index}`} style={{ flexShrink: 0, marginBottom: '5px' }}>
            <div style={{
              borderRadius: '8px',
              margin: '5px',
              height: 'calc(100% - 10px)', // Refined height adjustment
              display: 'flex',
              alignItems: 'stretch',
              boxSizing: 'border-box',

            }}>
              <PremiumSummary
                style={{
                  flex: 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              />
            </div>
          </div>
        );
      }
      */

      // After every fifth game, add a PlaySummary (unaffected by premium toggle)
      // if ((index + 1) % 5 === 0) {
      //   result.push(
      //     <div key={`play-${index}`} style={{ flexShrink: 0 }}>
      //       <div style={{
      //         border: '3px solid #58b047',
      //         borderRadius: '8px',
      //         padding: '10px',
      //         margin: '5px',
      //         display: 'flex',
      //         alignItems: 'stretch',
      //         justifyContent: 'center',
      //         boxSizing: 'border-box',
      //         width: '250px',
      //         backgroundColor: '#1a1a1a',
      //         height: '570px'
      //       }}>
      //         <PlaySummary
      //           style={{
      //             flex: 1,
      //             display: 'flex',
      //             alignItems: 'center',
      //             justifyContent: 'center'
      //           }}
      //         />
      //       </div>
      //     </div>
      //   );
      // }
    });

    return result;
  };

  // Keep the loadMoreGames function but remove the useCallback
  const loadMoreGames = () => {
    if (isLoadingGames || isLoadingMore || !hasMore) return;

    

    // For special tabs, pass the tab name as filterType
    const specialTabs = ['NEW', 'FOLLOWING', 'TRENDING'];

    if (specialTabs.includes(activeTab)) {
      fetchGamesForPortal(10, null, activeTab, false, selectedDifficulty);
    } else {
      // For tag tabs, get the tag key and pass it
      const tagKey = getTagKeyFromTabName(activeTab);
      fetchGamesForPortal(10, tagKey, null, false, selectedDifficulty);
    }
  };

  // Improved LoadMoreButton component logic
  const LoadMoreButton = () => {
    // Debug the state
    

    // Only hide if explicitly loading more games
    if (isLoadingMore) return null;

    // Always show if we have any games, even if hasMore is false
    // This gives users a way to try loading more even if backend thinks there are no more
    return (
      <div style={{
        flexShrink: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        marginLeft: '15px',
        marginRight: '15px',
        height: '100%'
      }}>
        <button
          onClick={loadMoreGames}
          style={{
            backgroundColor: 'gold',
            color: 'black',
            border: 'none',
            borderRadius: '5px',
            padding: '10px 20px',
            fontSize: '0.9rem',
            fontWeight: 'bold',
            cursor: hasMore ? 'pointer' : 'default',
            opacity: hasMore ? 1 : 0.7,
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
          }}
          disabled={!hasMore}
        >
          {hasMore ? "Load More" : "No More Games"} <i className="fa-solid fa-chevron-right"></i>
        </button>
      </div>
    );
  };

  return (
    <div className="app-container">
      {/* Welcome Modal */}
      {showWelcomeModal && (
        <WelcomeLoginModal onClose={() => setShowWelcomeModal(false)} />
      )}

      {/* Openings Modal */}
      {showOpeningsDropdown && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.7)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 10000,
        }}>
          <div
            style={{
              backgroundColor: '#191919',
              border: '3px solid #ffd700',
              borderRadius: '8px',
              width: '400px',
              maxWidth: '90%',
              maxHeight: '80vh',
              overflowY: 'auto',
              scrollbarWidth: 'thin',
              scrollbarColor: '#ffd700 transparent',
              boxShadow: '0 4px 20px rgba(0,0,0,0.8)',
              padding: '15px',
              position: 'relative',
              textTransform: 'uppercase',
            }}
          >
            {/* Close button */}
            <button
              style={{
                position: 'absolute',
                top: '10px',
                right: '10px',
                background: 'transparent',
                border: 'none',
                color: '#ffd700',
                fontSize: '20px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: '30px',
                height: '30px',
                borderRadius: '50%',
              }}
              onClick={() => {
                // Close the modal
                setShowOpeningsDropdown(false);

                // If nothing was selected, apply ALL OPENINGS filter
                if (!selectedOpening) {
                  setIsOpeningSelected(true);  // Mark as selected to display correct tab title
                  fetchAllOpeningsGames(25);
                }
              }}
            >
              <i className="fa-solid fa-xmark"></i>
            </button>

            <h3 style={{
              color: '#ffd700',
              textAlign: 'center',
              margin: '0 0 15px 0',
              fontSize: '1.4rem',
            }}>
              SELECT OPENING
            </h3>

            {/* Search input */}
            <div style={{ marginBottom: '15px' }}>
              <input
                type="text"
                placeholder="SEARCH OPENINGS..."
                value={openingSearchTerm}
                onChange={(e) => setOpeningSearchTerm(e.target.value)}
                style={{
                  width: '100%',
                  padding: '12px',
                  backgroundColor: '#111',
                  color: 'white',
                  border: '2px solid #333',
                  borderRadius: '4px',
                  fontSize: '1rem',
                  textTransform: 'uppercase',
                  boxSizing: 'border-box',
                }}
              />
            </div>

            <div style={{
              display: 'grid',
              gap: '5px',
              maxHeight: '50vh',
              overflowY: 'auto',
              padding: '5px',
              borderRadius: '4px',
              backgroundColor: '#111',
            }}>
              {/* ALL OPENINGS option */}
              <button
                style={{
                  width: '100%',
                  padding: '12px 15px',
                  textAlign: 'left',
                  backgroundColor: !selectedOpening ? 'rgba(255, 215, 0, 0.2)' : 'transparent',
                  color: !selectedOpening ? '#ffd700' : 'white',
                  border: 'none',
                  borderBottom: '1px solid rgba(255,255,255,0.1)',
                  cursor: 'pointer',
                  fontSize: '1rem',
                  fontWeight: !selectedOpening ? 'bold' : 'normal',
                  borderRadius: '4px',
                }}
                onClick={() => {
                  handleOpeningSelect('ALL OPENINGS');
                  setShowOpeningsDropdown(false);
                }}
              >
                ALL OPENINGS
              </button>

              {/* Opening options */}
              {getFilteredOpenings().filter(opening => opening !== 'ALL OPENINGS').map(opening => (
                <button
                  key={opening}
                  style={{
                    width: '100%',
                    padding: '12px 15px',
                    textAlign: 'left',
                    backgroundColor: selectedOpening === opening ? 'rgba(255, 215, 0, 0.2)' : 'transparent',
                    color: selectedOpening === opening ? '#ffd700' : 'white',
                    border: 'none',
                    borderBottom: '1px solid rgba(255,255,255,0.1)',
                    cursor: 'pointer',
                    fontSize: '1rem',
                    fontWeight: selectedOpening === opening ? 'bold' : 'normal',
                    borderRadius: '4px',
                    transition: 'background-color 0.2s',
                    '&:hover': {
                      backgroundColor: 'rgba(255, 215, 0, 0.1)',
                    }
                  }}
                  onClick={() => {
                    handleOpeningSelect(opening);
                    setShowOpeningsDropdown(false);
                  }}
                >
                  {opening}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      <div className="main-column explore-container" style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'flex-start',
        overflow: 'hidden',
        gap: '5px',
        maxWidth: isSidebarCollapsed ? 'calc(100vw - 125px)' : 'calc(100vw - 250px)',
        width: '100%',
        padding: '0 10px',
        boxSizing: 'border-box',
        background: 'linear-gradient(to bottom, #1a1a2e 0%, #16213e 50%, #0f3460 100%)',
        position: 'relative' // Added for premium toggle button positioning
      }}>

        <div style={{
          position: 'relative',
          width: '100%',
          maxWidth: '100%',
          margin: '0 auto',
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          minHeight: '500px',
          overflow: 'hidden',
        }}>
          <button
            style={{
              position: 'absolute',
              left: '0px', // Adjusted from -30px
              top: '50%',
              transform: 'translateY(-50%)',
              zIndex: 1,
              background: 'rgba(0,0,0,0.5)',
              border: '3px solid #57b047',
              borderRadius: '50%',
              width: '40px',
              height: '40px',
              cursor: 'pointer',
              color: '#57b047',
              fontSize: '1.5rem',
              zIndex: 10000,
            }}
            onClick={() => {
              const container = document.querySelector('.games-scroll-container');
              container.scrollBy({ left: -500, behavior: 'smooth' });
            }}
          >
            <i className="fa-solid fa-chevron-left"></i>
          </button>

          <div
            className="games-scroll-container"
            style={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              gap: '5px',
              width: 'calc(100% - 100px)',
              flex: 1,
              overflowX: 'auto',
              overflowY: 'hidden',
              paddingLeft: '50px',
              paddingRight: '50px',
              paddingBottom: '0px',
              marginBottom: '10px',
              scrollbarColor: '#57b047 transparent',
              scrollbarWidth: 'thin',
              position: 'relative',
            }}
          >
            {/* Games list */}
            {createGamesList(games)}

            {/* Loading indicator (inline) */}
            {isLoadingMore && (
              <div style={{
                flexShrink: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: '100px',
                height: '100%',
                color: 'white',

              }}>
                <div style={{
                  display: 'inline-block',
                  width: '30px',
                  height: '30px',
                  border: '3px solid rgba(255,215,0,0.3)',
                  borderRadius: '50%',
                  borderTopColor: 'gold',
                  animation: 'spin 1s ease-in-out infinite'
                }}></div>
                <style>{`
                  @keyframes spin {
                    to { transform: rotate(360deg); }
                  }
                `}</style>
              </div>
            )}

            {/* Load More Button */}
            <LoadMoreButton />
          </div>

          <button
            style={{
              position: 'absolute',
              right: '0px', // Adjusted from -30px
              top: '50%',
              transform: 'translateY(-50%)',
              zIndex: 1,
              background: 'rgba(0,0,0,0.5)',
              border: '3px solid #57b047',
              borderRadius: '50%',
              width: '40px',
              height: '40px',
              cursor: 'pointer',
              color: '#57b047',
              fontSize: '1.5rem',
            }}
            onClick={() => {
              const container = document.querySelector('.games-scroll-container');
              container.scrollBy({ left: 500, behavior: 'smooth' });
            }}
          >
            <i className="fa-solid fa-chevron-right"></i>
          </button>
        </div>

        <div style={{
          position: 'relative',
          width: '100%',
          maxWidth: '100%',
          margin: '5px auto',
          display: 'flex',
          alignItems: 'center',
          height: '45px', // Increased height slightly
          boxSizing: 'border-box',
          justifyContent: 'center', // Center the entire tab container
          borderTop: '3px solid #ffd700',    // Changed to direct border
          borderBottom: '3px solid #ffd700'  // Changed to direct border
        }}>
          <div
            ref={leftArrowRef}
            style={{
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              minWidth: '40px', // Give it a fixed width
              justifyContent: 'center'
            }}
          >
            <button
              style={{
                background: 'transparent',
                border: 'none',
                cursor: 'pointer',
                color: '#ffd700',
                fontSize: '1.5rem',
                padding: '0 10px',
                height: '100%',
                display: 'flex',
                alignItems: 'center',
                zIndex: 10000,
              }}
              onClick={() => {
                const container = document.querySelector('.tabs-scroll-container');
                container.scrollTo({ left: 0, behavior: 'smooth' });
              }}
            >
              <i className="fa-solid fa-chevron-left"></i>
            </button>
          </div>

          {/* Difficulty Selector */}
          <div
            ref={difficultyDropdownRef}
            style={{
              position: 'relative',
              height: '100%',
              marginRight: '10px',
              zIndex: 100, // Ensure it's above the tabs
            }}
          >
            <button
              style={{
                fontSize: '1rem',
                padding: '5px 15px',
                whiteSpace: 'nowrap',
                color: 'gold',
                fontWeight: 'bold',
                border: 'none',
                background: 'transparent',
                cursor: 'pointer',
                height: '100%',
                display: 'flex',
                alignItems: 'center',
                borderRadius: '0',
                textTransform: 'uppercase',
                width: '170px',
              }}
              onClick={() => setShowDifficultyDropdown(!showDifficultyDropdown)}
            >
              {selectedDifficulty}
              <i
                className={`fa-solid fa-chevron-${showDifficultyDropdown ? 'up' : 'down'}`}
                style={{ marginLeft: '8px' }}
              ></i>
            </button>

            {/* Dropdown options */}
            {showDifficultyDropdown && (
              <div
                style={{
                  position: 'absolute',
                  top: 'auto',
                  bottom: '100%', // Position above the selector
                  left: '5px',
                  backgroundColor: '#000',
                  border: '2px solid #ffd700',
                  borderRadius: '5px 5px 0px 0px',
                  boxShadow: '0 2px 10px rgba(0,0,0,0.5)',
                  width: '170px',
                  textTransform: 'uppercase',
                  zIndex: 101,
                }}
              >
                {Object.keys(DIFFICULTY).map(key => (
                  <button
                    key={key}
                    style={{
                      width: '100%',
                      padding: '8px 15px',
                      textAlign: 'left',
                      backgroundColor: key === selectedDifficulty ? 'rgba(255, 215, 0, 0.2)' : 'transparent',
                      color: key === selectedDifficulty ? '#ffd700' : 'white',
                      border: 'none',
                      borderBottom: '1px solid rgba(255,255,255,0.1)',
                      cursor: 'pointer',
                      fontSize: '1rem',
                      textTransform: 'uppercase',
                      fontWeight: key === selectedDifficulty ? 'bold' : 'normal',
                      '&:last-child': {
                        borderBottom: 'none',
                      },
                      '&:hover': {
                        backgroundColor: 'rgba(255, 215, 0, 0.1)',
                      }
                    }}
                    onClick={() => handleDifficultyChange(key)}
                  >
                    {DIFFICULTY[key].label}
                  </button>
                ))}
              </div>
            )}
          </div>

          <div
            className="tabs-container-wrapper"
            style={{
              position: 'relative',
              height: '100%',
              flex: '1',
              maxWidth: 'calc(100% - 150px)', // Reserve space for arrows and difficulty selector
              overflow: 'hidden' // Hide overflow
            }}
          >
            <div
              className="tabs-scroll-container"
              ref={scrollContainerRef}
              style={{
                display: 'flex',
                overflowX: 'auto',
                scrollBehavior: 'smooth',
                gap: '5px',
                height: '100%',
                width: '100%',
                alignItems: 'center',
                msOverflowStyle: 'none',
                scrollbarWidth: 'none',
                position: 'relative', // For positioning the borders
                '&::-webkit-scrollbar': { display: 'none' } // Hide scrollbar
              }}
            >
              {[
                'NEW', 'FOLLOWING', 'TRENDING',
                ...Object.keys(constants.TAGS)
                  .filter(tag => !HIDDEN_TAGS.includes(tag))
                  .map(tag => constants.TAGS[tag].label)
              ].map((tab) => {
                // Get display name for the tab (handles special case for openings)
                const displayTab = tab === 'Openings' ? getTabDisplayName('OPENINGS') : tab.toUpperCase();

                // Skip rendering the Openings tab if a specific opening is selected
                // This prevents duplicate UI when using the dedicated selector
                // if (tab === 'Openings' && selectedOpening && isOpeningSelected) {
                //   return null;
                // }

                return (
                  <div
                    key={tab}
                    ref={tab === 'Openings' ? openingsDropdownRef : null}
                    style={{
                      position: 'relative',
                      height: '100%',
                    }}
                  >
                    <button
                      className={`tab-button ${(displayTab === activeTab ||
                        (tab === 'Openings' && activeTab === 'OPENINGS') ||
                        (Object.keys(constants.TAGS).some(key =>
                          constants.TAGS[key].label === tab && key.toUpperCase() === activeTab)
                        )) ? 'active' : ''}`}
                      onClick={() => {
                        // Find the actual tag key when clicking a tab
                        if (['NEW', 'FOLLOWING', 'TRENDING'].includes(tab)) {
                          // Special tabs
                          handleTabClick(tab);
                        } else if (tab === 'Openings') {
                          // Openings tab
                          if (selectedOpening && isOpeningSelected) {
                            // If an opening is already selected, just open the modal
                            setShowOpeningsDropdown(true);
                          } else {
                            // Otherwise handle as normal
                            handleTabClick('OPENINGS');
                            // Show the modal
                            setShowOpeningsDropdown(true);
                          }
                        } else {
                          // Find the corresponding tag key
                          const tagKey = Object.keys(constants.TAGS).find(
                            key => constants.TAGS[key].label === tab
                          );
                          
                          // Pass the actual tag key to handleTabClick
                          handleTabClick(tagKey ? tagKey.toUpperCase() : tab);
                        }
                      }}
                      style={{
                        fontSize: (displayTab === activeTab ||
                          (tab === 'Openings' && activeTab === 'OPENINGS') ||
                          (Object.keys(constants.TAGS).some(key =>
                            constants.TAGS[key].label === tab && key.toUpperCase() === activeTab)
                          )) ? '1.25rem' : '1rem', // Enlarged active tab
                        padding: '5px 15px',
                        whiteSpace: 'nowrap',
                        color: (displayTab === activeTab ||
                          (tab === 'Openings' && activeTab === 'OPENINGS') ||
                          (Object.keys(constants.TAGS).some(key =>
                            constants.TAGS[key].label === tab && key.toUpperCase() === activeTab)
                          )) ? 'black' : 'gray',
                        fontWeight: (displayTab === activeTab ||
                          (tab === 'Openings' && activeTab === 'OPENINGS') ||
                          (Object.keys(constants.TAGS).some(key =>
                            constants.TAGS[key].label === tab && key.toUpperCase() === activeTab)
                          )) ? 'bold' : 'normal',
                        border: 'none',
                        transition: 'all 0.3s ease',
                        background: (displayTab === activeTab ||
                          (tab === 'Openings' && activeTab === 'OPENINGS') ||
                          (Object.keys(constants.TAGS).some(key =>
                            constants.TAGS[key].label === tab && key.toUpperCase() === activeTab)
                          )) ? '#ffd700' : 'transparent',
                        cursor: 'pointer',
                        height: '100%',
                        display: 'flex',
                        textTransform: 'uppercase',
                        alignItems: 'center',
                        position: (displayTab === activeTab ||
                          (tab === 'Openings' && activeTab === 'OPENINGS') ||
                          (Object.keys(constants.TAGS).some(key =>
                            constants.TAGS[key].label === tab && key.toUpperCase() === activeTab)
                          )) ? 'sticky' : 'relative', // Make active tab sticky
                        left: (displayTab === activeTab ||
                          (tab === 'Openings' && activeTab === 'OPENINGS') ||
                          (Object.keys(constants.TAGS).some(key =>
                            constants.TAGS[key].label === tab && key.toUpperCase() === activeTab)
                          )) ? 0 : 'auto', // Stick to the left
                        right: (displayTab === activeTab ||
                          (tab === 'Openings' && activeTab === 'OPENINGS') ||
                          (Object.keys(constants.TAGS).some(key =>
                            constants.TAGS[key].label === tab && key.toUpperCase() === activeTab)
                          )) && tab !== 'NEW' ? 0 : 'auto', // Stick to the right if it's not the first tab
                        zIndex: (displayTab === activeTab ||
                          (tab === 'Openings' && activeTab === 'OPENINGS') ||
                          (Object.keys(constants.TAGS).some(key =>
                            constants.TAGS[key].label === tab && key.toUpperCase() === activeTab)
                          )) ? 1 : 0 // Ensure it's above other tabs
                      }}
                    >
                      {displayTab}
                      {tab === 'Openings' && (
                        <i
                          className={`fa-solid fa-chevron-${showOpeningsDropdown ? 'up' : 'down'}`}
                          style={{ marginLeft: '8px', fontSize: '0.8rem' }}
                        ></i>
                      )}
                    </button>
                  </div>
                );
              })}
            </div>
          </div>

          <div
            ref={rightArrowRef}
            style={{
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              marginLeft: '10px', // Add space between tabs and arrow
              minWidth: '40px', // Give it a fixed width
              justifyContent: 'center'
            }}
          >
            <button
              style={{
                background: 'transparent',
                border: 'none',
                cursor: 'pointer',
                color: '#ffd700',
                fontSize: '1.5rem',
                padding: '0 10px',
                height: '100%',
                display: 'flex',
                alignItems: 'center',
              }}
              onClick={() => {
                const container = document.querySelector('.tabs-scroll-container');
                container.scrollBy({ left: 300, behavior: 'smooth' });
              }}
            >
              <i className="fa-solid fa-chevron-right"></i>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const mapStateToProps = (state) => ({
  user: state.user
});

export default connect(mapStateToProps)(Portal);
